import {useEffect,useState,type FormEvent} from 'react';
type Customer={customerId:string;displayName:string;email:string|null;
  phone:string|null;homeLocationCode:string|null};
type Detail={id:string;name:string;email:string|null;phone:string|null;
  version:number;actorId:string|null;homeLocation:string|null;credits:number;
  appointments:number;waivers:number;cashSales:number;testOrders:number;
  customerNotes:number;importantNotes:number;
  recordedCashSpentCents:number;testPaidCents:number};
type Preview={left:Detail;right:Detail;matchedFields:string[];
  warning:boolean;canMerge:boolean;blockCode:string|null};
type SearchReply={ok:boolean;data?:{customers:Customer[]};error?:{code:string}};
type PreviewReply={ok:boolean;data?:Preview;error?:{code:string}};

function PersonPicker({location,heading,selected,onPick}:{
  location:string;heading:string;selected:Customer|null;
  onPick:(c:Customer|null)=>void;
}){
  const [query,setQuery]=useState(''),[results,setResults]=useState<Customer[]>([]);
  const [notice,setNotice]=useState(''),[busy,setBusy]=useState(false);
  useEffect(()=>{setQuery('');setResults([]);setNotice('');},[location]);
  const search=async(event:FormEvent)=>{
    event.preventDefault();if(query.trim().length<2)return;
    setBusy(true);setNotice('');
    try{
      const response=await fetch('/api/v1/staff/customers?'+new URLSearchParams({
        location,q:query.trim()}),{credentials:'same-origin',cache:'no-store'});
      const body=await response.json() as SearchReply;
      if(!response.ok||!body.ok)throw Error('Unable to search customer accounts.');
      setResults(body.data?.customers??[]);
      if(!body.data?.customers.length)setNotice('No matching customer profiles.');
    }catch(error){setNotice(error instanceof Error?error.message:'Search failed.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-merge-person" aria-label={heading}>
    <h2>{heading}</h2>
    {selected?<div>
      <strong>{selected.displayName}</strong>
      <p>{selected.email??''} {selected.phone??''}</p>
      <button type="button" onClick={()=>{onPick(null);setResults([]);}}>Clear Selection</button>
    </div>:<form onSubmit={e=>void search(e)}>
      <label>Find a customer
        <input type="search" value={query} minLength={2}
          onChange={e=>setQuery(e.target.value)}
          aria-label={heading+' customer search'} placeholder="Name, email or phone"/>
      </label>
      <button type="submit" disabled={busy||query.trim().length<2}>
        {busy?'Searching…':'Search'}</button>
      {notice&&<p role="status">{notice}</p>}
      <div role="list" aria-label={heading+' search results'}>
        {results.map(c=><button type="button" role="listitem" key={c.customerId}
          onClick={()=>{onPick(c);setResults([]);}}>
          {c.displayName}{c.email?' · '+c.email:''}
          {c.phone?' · '+c.phone:''}
        </button>)}
      </div>
    </form>}
  </section>;
}
const dollar=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD',
}).format(cents/100);
function Summary({person}:{person:Detail}){
  return <div className="ecfs-merge-summary">
    <h3>{person.name}</h3>
    <dl>
      <dt>Account ID</dt><dd>{person.id}</dd>
      <dt>Email</dt><dd>{person.email||'None'}</dd>
      <dt>Phone</dt><dd>{person.phone||'None'}</dd>
      <dt>Home Location</dt><dd>{person.homeLocation||'None'}</dd>
      <dt>Available Service Credits</dt><dd>{person.credits}</dd>
      <dt>Reservations</dt><dd>{person.appointments}</dd>
      <dt>Signed Waivers (including prior merges)</dt><dd>{person.waivers}</dd>
      <dt>Customer Notes</dt><dd>{person.customerNotes}</dd>
      <dt>Important Notes</dt><dd>{person.importantNotes}</dd>
      <dt>Cash Sales</dt><dd>{person.cashSales}</dd>
      <dt>Recorded PWA Cash Spend</dt><dd>{dollar(person.recordedCashSpentCents)}</dd>
      <dt>Stripe TEST Paid Orders</dt><dd>{dollar(person.testPaidCents)}</dd>
      <dt>Test Checkout Orders</dt><dd>{person.testOrders}</dd>
    </dl>
  </div>;
}
export function CustomerMergeWorkspace({locationCode}:{locationCode:string}){
  const [left,setLeft]=useState<Customer|null>(null);
  const [right,setRight]=useState<Customer|null>(null);
  const [preview,setPreview]=useState<Preview|null>(null);
  const [error,setError]=useState('');
  const [busy,setBusy]=useState(false);
  const [confirming,setConfirming]=useState(false);
  const [done,setDone]=useState(false);
  useEffect(()=>{
    setPreview(null);setConfirming(false);setError('');setDone(false);
    if(!left||!right||left.customerId===right.customerId)return;
    const controller=new AbortController();
    setBusy(true);
    void fetch('/api/v1/staff/customer-merge/preview?'+new URLSearchParams({
      location:locationCode,leftId:left.customerId,rightId:right.customerId,
    }),{credentials:'same-origin',cache:'no-store',signal:controller.signal})
      .then(async response=>{
        const body=await response.json() as PreviewReply;
        if(!response.ok||!body.ok||!body.data)
          throw Error(body.error?.code??'Merge review unavailable.');
        if(!controller.signal.aborted)setPreview(body.data);
      })
      .catch(e=>{if(!controller.signal.aborted)setError(
        e instanceof Error?e.message:'Merge review unavailable.');})
      .finally(()=>{if(!controller.signal.aborted)setBusy(false);});
    return ()=>controller.abort();
  },[locationCode,left,right]);
  const reset=()=>{setLeft(null);setRight(null);setPreview(null);
    setError('');setConfirming(false);setDone(false);};
  const merge=async()=>{
    if(!preview?.canMerge||!confirming||busy)return;
    setBusy(true);setError('');
    try{
      const response=await fetch('/api/v1/staff/customer-merge',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:locationCode,leftId:preview.left.id,rightId:preview.right.id,
          leftVersion:preview.left.version,rightVersion:preview.right.version,
        }),
      });
      const body=await response.json() as {ok:boolean;data?:{merged:boolean};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data?.merged)
        throw Error(body.error?.code??'The accounts were not merged.');
      setDone(true);setConfirming(false);
    }catch(e){setError(e instanceof Error?e.message:'The accounts were not merged.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-customer-merge" aria-label="Customer Merge Tool">
    <h1>Merge User Accounts</h1>
    <p>Choose the duplicate account on the LEFT and the account to KEEP on the RIGHT.
      The RIGHT account survives.</p>
    <div className="ecfs-merge-actions">
      <button type="button" onClick={()=>{setLeft(right);setRight(left);}}>
        Swap Accounts</button>
      <button type="button" onClick={reset}>Clear and Start Over</button>
    </div>
    <div className="ecfs-merge-columns">
      <PersonPicker location={locationCode} heading="MERGE THIS ACCOUNT (LEFT)"
        selected={left} onPick={setLeft}/>
      <PersonPicker location={locationCode} heading="KEEP THIS ACCOUNT (RIGHT)"
        selected={right} onPick={setRight}/>
    </div>
    {left&&right&&left.customerId===right.customerId&&
      <p role="alert">Choose two different accounts.</p>}
    {busy&&!done&&<p role="status">Reviewing account records…</p>}
    {error&&<p role="alert">{error}</p>}
    {preview&&<section aria-label="Review accounts to merge">
      <div className="ecfs-merge-columns">
        <Summary person={preview.left}/>
        <Summary person={preview.right}/>
      </div>
      <p>Recorded PWA cash amounts and Stripe TEST payments are shown separately.
        They are not a complete FloatHelm lifetime-spend history.</p>
      <p>Matching identity fields: {preview.matchedFields.join(', ')||'None'}</p>
      {preview.warning&&<p role="alert">Only one identity field matches.
        Verify both accounts belong to the same person before merging.</p>}
      {preview.blockCode&&<p role="alert">Cannot merge these accounts:
        {' '+(preview.blockCode==='MERGE_IDENTITY_MISMATCH'?
          'no matching identity fields.':preview.blockCode==='MERGE_UNSUPPORTED_CUSTOMER_LINKS'?
          'linked customer history cannot yet be transferred safely.':
          'linked account identity conflict.')}</p>}
      {!done&&preview.canMerge&&!confirming&&
        <button type="button" onClick={()=>setConfirming(true)}>
          Merge Left Account Into Right</button>}
      {!done&&preview.canMerge&&confirming&&<div role="group"
        aria-label="Confirm customer merge">
        <p>Merge <strong>{preview.left.name}</strong> (LEFT) into
          <strong> {preview.right.name}</strong> (RIGHT)?
          The RIGHT account survives and the LEFT account is archived.</p>
        <button type="button" disabled={busy} onClick={()=>void merge()}>
          Confirm Merge Left Account Into Right</button>
        <button type="button" disabled={busy} onClick={()=>setConfirming(false)}>
          Cancel</button>
      </div>}
      {done&&<p role="status">Accounts merged. The RIGHT account was retained.</p>}
    </section>}
  </section>;
}
